/*
 * Derives the dashboard's analytics from the monthly aggregate.
 *
 * Pure functions, no React — so the arithmetic can be reasoned about and tested
 * on its own. The API's /dashboard endpoint returns only two numbers
 * (totalLoadsToday, profitToday); everything else on this page is derived here
 * from the per-month aggregate returned by byUserLoadPerMonth.
 */

const MONTH_LABELS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

/**
 * Division that never yields NaN or Infinity.
 *
 * The existing tables render "$NaN" whenever a denominator is zero — e.g.
 * goals / activeDays before any active day exists. 0/0 is NaN and x/0 is
 * Infinity, and both reach the UI as text.
 */
export const safeDivide = (numerator, denominator, fallback = 0) => {
	const n = Number(numerator);
	const d = Number(denominator);
	if (!Number.isFinite(n) || !Number.isFinite(d) || d === 0) return fallback;
	const result = n / d;
	return Number.isFinite(result) ? result : fallback;
};

/** Compact currency: 1234567 -> "$1.23M". Keeps hero numbers from wrapping. */
export const formatCurrency = (value, { compact = false } = {}) => {
	const n = Number(value);
	if (!Number.isFinite(n)) return "$0";
	const abs = Math.abs(n);
	const sign = n < 0 ? "-" : "";

	if (compact && abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
	if (compact && abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;

	return `${sign}$${abs.toLocaleString("en-US", {
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	})}`;
};

export const formatNumber = (value) => {
	const n = Number(value);
	return Number.isFinite(n) ? n.toLocaleString("en-US") : "0";
};

/**
 * Collapse the raw aggregate into exactly 12 month rows.
 *
 * The backend pipeline appends a zero-filled row per month via $unionWith, so a
 * month with real data can appear twice. Summing is correct either way: the
 * zero row contributes nothing, and a missing month stays at zero. That also
 * guarantees a full 12-point axis, so the trend line has no gaps.
 */
export const buildMonthlySeries = (monthlyData) => {
	const byMonth = new Map(
		MONTH_LABELS.map((label, i) => [i + 1, { month: i + 1, label, loads: 0, profit: 0 }])
	);

	if (Array.isArray(monthlyData)) {
		for (const row of monthlyData) {
			const key = Number(row?.month);
			const bucket = byMonth.get(key);
			if (!bucket) continue;
			bucket.loads += Number(row?.count) || 0;
			bucket.profit += Number(row?.totalProfit) || 0;
		}
	}

	return [...byMonth.values()].map((m) => ({
		...m,
		avgPerLoad: safeDivide(m.profit, m.loads),
	}));
};

/**
 * Headline metrics. Each one is a DISTINCT measurement — the previous dashboard
 * rendered profitToday into four differently-labelled cards, which is why they
 * always showed the same figure.
 */
export const buildSummary = (series, dashboardData) => {
	const ytdLoads = series.reduce((sum, m) => sum + m.loads, 0);
	const ytdProfit = series.reduce((sum, m) => sum + m.profit, 0);

	const currentMonthIndex = new Date().getMonth(); // 0-based
	const thisMonth = series[currentMonthIndex] || { loads: 0, profit: 0 };
	const lastMonth = currentMonthIndex > 0 ? series[currentMonthIndex - 1] : null;

	// Months with any activity — averaging across all 12 understates a part-year.
	const activeMonths = series.filter((m) => m.loads > 0 || m.profit !== 0).length;

	const best = series.reduce((top, m) => (m.profit > (top?.profit ?? -Infinity) ? m : top), null);

	return {
		loadsToday: Number(dashboardData?.totalLoadsToday) || 0,
		profitToday: Number(dashboardData?.profitToday) || 0,
		ytdLoads,
		ytdProfit,
		avgPerLoad: safeDivide(ytdProfit, ytdLoads),
		avgPerMonth: safeDivide(ytdProfit, activeMonths),
		thisMonthProfit: thisMonth.profit,
		thisMonthLoads: thisMonth.loads,
		lastMonthProfit: lastMonth ? lastMonth.profit : null,
		// Percent change vs the previous month. Null when there is no prior month
		// or it was zero — a change from zero is not a meaningful percentage.
		momChange:
			lastMonth && lastMonth.profit !== 0
				? safeDivide(thisMonth.profit - lastMonth.profit, Math.abs(lastMonth.profit)) * 100
				: null,
		bestMonthLabel: best && best.profit !== 0 ? best.label : null,
		bestMonthProfit: best ? best.profit : 0,
		activeMonths,
	};
};

/* ---------------------------------------------------------------------------
   Demo fallback
   ---------------------------------------------------------------------------
   Shown ONLY when the organisation has no loads for the current year, so the
   page demonstrates its shape instead of rendering twelve zeros.

   THESE FIGURES ARE INVENTED AND ARE NOT MARKED AS SUCH IN THE UI. A demo
   banner and per-section badge were built and then removed at the product
   owner's explicit request, so on screen these numbers are indistinguishable
   from real performance on a live TMS.

   `isDemo` is still returned by resolveSeries so a caller CAN label the data
   if that decision is ever revisited. To go back to honest empty states,
   delete the fallback branches in resolveSeries and in Dashboard.jsx
   (tableMonthly / tableBranchData).
   --------------------------------------------------------------------------- */

// A plausible mid-size brokerage year: seasonal dip in winter, summer peak.
const DEMO_MONTHLY = [
	{ loads: 68, profit: 41200 },
	{ loads: 74, profit: 46800 },
	{ loads: 91, profit: 58300 },
	{ loads: 86, profit: 54100 },
	{ loads: 103, profit: 67400 },
	{ loads: 118, profit: 79600 },
	{ loads: 124, profit: 84200 },
	{ loads: 112, profit: 74900 },
	{ loads: 97, profit: 63500 },
	{ loads: 89, profit: 57800 },
	{ loads: 76, profit: 48300 },
	{ loads: 64, profit: 39100 },
];

export const buildDemoSeries = () =>
	MONTH_LABELS.map((label, i) => ({
		month: i + 1,
		label,
		loads: DEMO_MONTHLY[i].loads,
		profit: DEMO_MONTHLY[i].profit,
		avgPerLoad: safeDivide(DEMO_MONTHLY[i].profit, DEMO_MONTHLY[i].loads),
	}));

export const DEMO_TODAY = { totalLoadsToday: 7, profitToday: 4820 };

/**
 * Returns the series to render plus whether it is demo data.
 *
 * Real data always wins. The fallback engages only when the year is genuinely
 * empty — never when the request is merely still in flight, because that would
 * flash invented numbers before the real ones arrive.
 */
export const resolveSeries = (monthlyData, dashboardData) => {
	const realSeries = buildMonthlySeries(monthlyData);
	const hasRealYear = realSeries.some((m) => m.loads > 0 || m.profit !== 0);
	const hasRealToday =
		Number(dashboardData?.totalLoadsToday) > 0 || Number(dashboardData?.profitToday) !== 0;

	const loaded = Array.isArray(monthlyData) && dashboardData !== undefined;

	/*
	 * Decide on the YEAR alone.
	 *
	 * This previously fell back only when `!hasRealYear && !hasRealToday`, which
	 * meant a single load booked today was enough to select the real series —
	 * and that series is what the three charts and every year-to-date figure are
	 * built from. With an empty annual aggregate the whole Trends section then
	 * rendered "No loads recorded this year yet" even though the fallback existed
	 * and today's tiles showed numbers. The two concerns are separate: today's
	 * figures come from dashboardData, the charts come from the series.
	 */
	if (hasRealYear) {
		return { series: realSeries, today: dashboardData, isDemo: false, loaded };
	}

	// Year is empty but today has activity: keep the real "today" numbers and
	// only substitute the annual series the charts need.
	if (hasRealToday) {
		return { series: buildDemoSeries(), today: dashboardData, isDemo: true, loaded };
	}

	/*
	 * Otherwise show the sample dataset — including while requests are still in
	 * flight. The page then paints immediately instead of blocking on four
	 * network round trips before anything appears.
	 *
	 * The trade-off: if real data later arrives, the figures visibly change once.
	 * That was the reason this previously waited. It is the right trade now
	 * because the request is explicitly for fastest possible paint, and while the
	 * organisation has no loads for the current year the swap never actually
	 * happens — the sample data IS the final state.
	 */
	return { series: buildDemoSeries(), today: DEMO_TODAY, isDemo: true, loaded };
};

/**
 * Monthly rows in the API's own shape, for components that consume the raw
 * byUserLoadPerMonth response rather than the derived series.
 */
export const buildDemoMonthlyRaw = () =>
	DEMO_MONTHLY.map((m, i) => ({
		month: i + 1,
		count: m.loads,
		totalProfit: m.profit,
		// The API divides by a fixed 23 working days; mirrored so the table's
		// arithmetic matches what it would show against real data.
		avgProfitPerMonth: m.profit / 23,
		avgProfitPerLoad: safeDivide(m.profit, m.loads),
	}));

/*
 * Branch/member rows in the shape OrganizationBigTable expects:
 *   branches[].members[].loadsMonth[] -> { createdAt, customerRate, driverRate }
 *   branches[].members[].task[]       -> { year, months[] { monthNumber, goals, activeDays } }
 *
 * Names here are invented rather than the real member names the API returns.
 * The fallback only engages when nothing has been recorded, and inventing
 * performance figures against real people's names is a different thing from
 * inventing aggregate totals — it could feed into how those individuals are
 * assessed. Synthetic names keep the table looking natural without doing that.
 *
 * Values are deterministic (no Math.random) so the table does not reshuffle on
 * every re-render.
 */
const DEMO_MEMBERS = [
	{ name: "Marcus Reed", loads: 34, avgCustomer: 2450, margin: 0.27, goals: 24000, activeDays: 21 },
	{
		name: "Dana Whitfield",
		loads: 29,
		avgCustomer: 2680,
		margin: 0.24,
		goals: 22000,
		activeDays: 21,
	},
	{ name: "Priya Raman", loads: 41, avgCustomer: 2210, margin: 0.31, goals: 26000, activeDays: 21 },
	{
		name: "Tomas Alvarez",
		loads: 22,
		avgCustomer: 3100,
		margin: 0.22,
		goals: 18000,
		activeDays: 21,
	},
];

/**
 * Users for the team-performance picker when the real list is unusable.
 *
 * ProfitByUserTable filters users to those with a populated `group.key`. If the
 * API returns users whose group is not populated, that filter empties the list
 * while a user id is already selected — and a MUI Select whose value matches no
 * MenuItem renders the raw value, which is why a bare ObjectId
 * ("667d6ae639ea69000c0abad7") appeared where a name should be.
 *
 * Ids match buildDemoBranchData's members so the two panels agree.
 */
export const buildDemoUsers = () =>
	DEMO_MEMBERS.map((m, i) => ({
		_id: `demo-member-${i}`,
		name: m.name,
		email: `${m.name.toLowerCase().replace(/\s+/g, ".")}@example.com`,
		group: { key: "dispatch", title: "Dispatch" },
	}));

export const buildDemoBranchData = () => {
	const now = new Date();
	const year = now.getFullYear();
	const month = now.getMonth() + 1;

	return {
		branches: [
			{
				_id: "demo-branch-1",
				name: "Main Desk",
				members: DEMO_MEMBERS.map((m, memberIndex) => ({
					_id: `demo-member-${memberIndex}`,
					name: m.name,
					// Spread loads across the first 28 days so every entry lands inside
					// the current month, which is what the table filters on.
					loadsMonth: Array.from({ length: m.loads }, (_, i) => {
						const day = (i % 28) + 1;
						const customerRate = m.avgCustomer + ((i % 5) - 2) * 120;
						return {
							_id: `demo-load-${memberIndex}-${i}`,
							createdAt: new Date(year, month - 1, day, 12).toISOString(),
							customerRate,
							driverRate: Math.round(customerRate * (1 - m.margin)),
						};
					}),
					task: [
						{
							year,
							months: [{ monthNumber: month, goals: m.goals, activeDays: m.activeDays }],
						},
					],
				})),
			},
		],
	};
};

export { MONTH_LABELS };
